import React, { useState, useEffect, useCallback } from 'react';
import './DiasData.css'; // Ajusta la ruta al CSS
import Swal from 'sweetalert2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { apiRaw } from '../../../api';

export default function TallerDiasData({ tallerId }) {
    const [dias, setDias] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const diasSemana = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];

    const cargarDias = useCallback(async () => {
        if (!tallerId) return;

        try {
            const response = await apiRaw(`/dias?idTaller=${tallerId}&tipo_recurso=taller`, {
                method: 'GET'
            });
            const data = await response.json();

            if (data.dias_horarios && data.dias_horarios.length > 0) {
                // Tomamos el primer elemento del array
                const horarioData = data.dias_horarios[0];
                //console.log('Datos recibidos de la API:', horarioData);

                const parsedDias = {
                    ...horarioData,
                    idTallerDiasHorarios: horarioData.idDiasHorarios, // Aseguramos que el ID esté correctamente mapeado
                    dias: typeof horarioData.dias === 'string' ? JSON.parse(horarioData.dias) : (horarioData.dias || [])
                };

                //console.log('Días parseados:', parsedDias);

                // Asegurarse de que todos los días de la semana estén presentes
                const diasCompletos = diasSemana.map(dia => {
                    const diaEncontrado = Array.isArray(parsedDias.dias) ?
                        parsedDias.dias.find(d => d.dia === dia) : null;

                    return diaEncontrado ? {
                        ...diaEncontrado,
                        selected: true
                    } : {
                        dia,
                        horarios: [],
                        selected: false
                    };
                });

                //console.log('Días completos:', diasCompletos);
                setDias({ ...parsedDias, dias: diasCompletos });
            } else {
                const diasIniciales = diasSemana.map(dia => ({
                    dia,
                    horarios: [],
                    selected: false
                }));
                setDias({
                    idTaller: parseInt(tallerId),
                    dias: diasIniciales
                });
            }
        } catch (error) {
            console.error('Error al cargar días del taller:', error);
            toast.error('Error al cargar los horarios del taller');
        } finally {
            setLoading(false);
        }
    }, [tallerId]);

    useEffect(() => {
        cargarDias();
    }, [cargarDias]);

    const agregarHorarioAcorde = (diaNombre) => {
        setDias(prevDias => {
            const nuevosDias = { ...prevDias };
            const diaSeleccionado = nuevosDias.dias.find(d => d.dia === diaNombre);

            if (diaSeleccionado) {
                const ultimoHorario = diaSeleccionado.horarios[diaSeleccionado.horarios.length - 1];
                let nuevaHoraInicio = '09:00';
                let nuevaHoraFin = '09:30';

                if (ultimoHorario) {
                    const [horaFinHoras, horaFinMinutos] = ultimoHorario.horaFin.split(':').map(Number);
                    const nuevaHoraInicioDate = new Date();
                    nuevaHoraInicioDate.setHours(horaFinHoras, horaFinMinutos);

                    const nuevaHoraFinDate = new Date(nuevaHoraInicioDate);
                    nuevaHoraFinDate.setMinutes(nuevaHoraInicioDate.getMinutes() + 30);

                    nuevaHoraInicio = nuevaHoraInicioDate.toTimeString().slice(0, 5);
                    nuevaHoraFin = nuevaHoraFinDate.toTimeString().slice(0, 5);
                }

                diaSeleccionado.horarios.push({
                    horaInicio: nuevaHoraInicio,
                    horaFin: nuevaHoraFin,
                });
                diaSeleccionado.selected = true;
            }

            return nuevosDias;
        });
    };

    const replicarHorarios = (diaNombre) => {
        setDias(prevDias => {
            const diaSeleccionado = prevDias.dias.find(d => d.dia === diaNombre);
            if (!diaSeleccionado || diaSeleccionado.horarios.length === 0) {
                toast.error(`No hay horarios para replicar desde ${diaNombre}.`, { autoClose: 500 });
                return prevDias;
            }

            const nuevosDias = { ...prevDias };
            nuevosDias.dias = nuevosDias.dias.map(dia => {
                if (dia.dia !== diaNombre && dia.selected) {
                    return { ...dia, horarios: [...diaSeleccionado.horarios] };
                }
                return dia;
            });

            toast.success(`Horarios replicados desde ${diaNombre}.`, { autoClose: 500 });
            return nuevosDias;
        });
    };

    const handleHorarioChange = (diaNombre, horarioIndex, field, value) => {
        setDias(prevDias => {
            const nuevosDias = { ...prevDias };
            const diaSeleccionado = nuevosDias.dias.find(d => d.dia === diaNombre);

            if (diaSeleccionado && diaSeleccionado.horarios[horarioIndex]) {
                diaSeleccionado.horarios[horarioIndex][field] = value;
            }

            return nuevosDias;
        });
    };

    const eliminarHorario = (diaNombre, horarioIndex) => {
        setDias(prevDias => {
            const nuevosDias = { ...prevDias };
            const diaSeleccionado = nuevosDias.dias.find(d => d.dia === diaNombre);

            if (diaSeleccionado) {
                diaSeleccionado.horarios = diaSeleccionado.horarios.filter((_, i) => i !== horarioIndex);
            }

            return nuevosDias;
        });
    };

    const handleCheckboxChange = (diaNombre) => {
        setDias(prevDias => {
            const nuevosDias = { ...prevDias };
            const diaSeleccionado = nuevosDias.dias.find(d => d.dia === diaNombre);

            if (diaSeleccionado) {
                diaSeleccionado.selected = !diaSeleccionado.selected;
                if (!diaSeleccionado.selected) {
                    diaSeleccionado.horarios = [];
                }
            }

            return nuevosDias;
        });
    };

    const handleUpdate = async () => {
        if (!dias) return;

        // Validar que cada día seleccionado tenga al menos un horario
        for (const dia of dias.dias) {
            if (dia.selected && dia.horarios.length === 0) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: `Debe agregar al menos un horario para el día ${dia.dia}`,
                });
                return;
            }
        }

        try {
            const diasFiltrados = dias.dias.filter(dia => dia.selected);
            const payload = {
                idTaller: parseInt(tallerId),
                dias: JSON.stringify(diasFiltrados),
                estado: 'Activo',
                tipo_recurso: 'taller',
            };

            const url = dias.idTallerDiasHorarios
                ? `/dias/${dias.idTallerDiasHorarios}`
                : `/dias`;

            const response = await apiRaw(url, {
                method: dias.idTallerDiasHorarios ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (data.error) {
                throw new Error(data.error);
            }

            Swal.fire('¡Guardado!', data.mensaje || 'Horarios actualizados correctamente', 'success');
            await cargarDias();
        } catch (error) {
            console.error('Error al guardar:', error);
            Swal.fire('Error', error.message || 'Error al guardar los cambios', 'error');
        }
    };

    const eliminar = (idTallerDiasHorarios) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: '¡No podrás revertir esta acción!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await apiRaw(`/dias/${idTallerDiasHorarios}`, {
                        method: 'DELETE',
                    });
                    const data = await response.json();

                    if (data.error) {
                        throw new Error(data.error);
                    }

                    Swal.fire('¡Eliminado!', data.mensaje || 'Horarios eliminados correctamente', 'success');
                    await cargarDias();
                } catch (error) {
                    console.error('Error al eliminar:', error);
                    Swal.fire('Error', error.message || 'Error al eliminar los horarios', 'error');
                }
            }
        });
    };

    if (loading) {
        return <div className="loading">Cargando horarios del taller...</div>;
    }

    if (!dias) {
        return <div>No se pudieron cargar los horarios del taller.</div>;
    }

    return (
        <div className='DiasDataContainer'>
            <div className='NewContain'>
                <div className="diasContainer">
                    {dias.dias.map(diaItem => (
                        <div key={diaItem.dia} className="diaGrp">
                            <div className='diaChek'>
                                <input
                                    type="checkbox"
                                    checked={!!diaItem.selected}
                                    onChange={() => handleCheckboxChange(diaItem.dia)}
                                />
                                <span>{diaItem.dia.charAt(0).toUpperCase() + diaItem.dia.slice(1)}</span>
                            </div>


                            <div className="horarios">
                                {diaItem.horarios.map((horario, index) => (
                                    <div key={index} className="horario">
                                        <input
                                            type="time"
                                            value={horario.horaInicio || ''}
                                            onChange={(e) => handleHorarioChange(diaItem.dia, index, 'horaInicio', e.target.value)}
                                        />
                                        <span>hasta</span>
                                        <input
                                            type="time"
                                            value={horario.horaFin || ''}
                                            onChange={(e) => handleHorarioChange(diaItem.dia, index, 'horaFin', e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            className='btnMenosHora'
                                            onClick={() => eliminarHorario(diaItem.dia, index)}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </div>
                                ))}
                                <div className="horario-actions">
                                    <button
                                        type="button"
                                        className='btnMoreHora'
                                        onClick={() => agregarHorarioAcorde(diaItem.dia)}
                                    >
                                        Agregar horario
                                    </button>
                                    <button
                                        type="button"
                                        className='btnReplicar'
                                        onClick={() => replicarHorarios(diaItem.dia)}
                                    >
                                        Replicar a otros días
                                    </button>
                                </div>
                            </div>

                        </div>
                    ))}
                </div>
                <div className='deFlexBtnTienda'>
                    <button
                        type="button"
                        className='btnPost'
                        onClick={handleUpdate}
                        disabled={loading}
                    >
                        {loading ? 'Guardando...' : 'Guardar horarios'}
                    </button>
                    {dias.idTallerDiasHorarios && (
                        <button
                            className='eliminarBtn'
                            onClick={() => eliminar(dias.idTallerDiasHorarios)}
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    )}
                </div>
            </div>
            <ToastContainer position="bottom-right" autoClose={3000} />
        </div>
    );
}
